import { Injectable } from '@angular/core';
import { JsonConvert, ValueCheckingMode } from 'json2typescript';
import { FeaturesFactory, FeaturesTreeReaderFactory, RequestRoot } from 'types';
import {
    ApplicationNotification,
    StatusChangeNotification,
} from '../model/application-notification';

@Injectable()
export class MobileStorageManager {
    private readonly converter: JsonConvert;
    private readonly STORAGE_APP_NOTIFICATIONS =
        'MobileApplicationNotifications';
    private readonly STORAGE_SUMMARY_KEY = 'MobileRequestsSummary';
    private readonly STORAGE_REQUEST_KEY_PREFIX = 'MobileRequest#';
    private readonly CURRENT_NOTIFICATION_VERSION = '1';
    private readonly CURRENT_REQUEST_DATA_VERSION = '2';
    private readonly CURRENT_REQUEST_SUMMARY_VERSION = '1';
    private notificationTypes = new Map<
        string,
        new () => ApplicationNotification
    >();

    constructor(
        private featuresF: FeaturesFactory,
        private featureTreeReaderF: FeaturesTreeReaderFactory
    ) {
        this.converter = new JsonConvert();
        this.converter.valueCheckingMode = ValueCheckingMode.DISALLOW_NULL;
        this.notificationTypes.set(
            StatusChangeNotification.name,
            StatusChangeNotification
        );
    }

    loadNotificationListFromStorage(): ApplicationNotification[] {
        let notifications: ApplicationNotification[];
        let item = localStorage.getItem(this.STORAGE_APP_NOTIFICATIONS);

        if (item) {
            let json = JSON.parse(item);
            notifications = json.data.map((e: any) => {
                let type = this.notificationTypes.get(e.key);
                return this.converter.deserialize(e.value, type);
            });
        } else {
            notifications = [];
            this.storeNotificationListToStorage(notifications);
        }
        return notifications;
    }

    loadNotificationItemFromStorage(
        notificationItemId: string
    ): ApplicationNotification {
        let list = this.loadNotificationListFromStorage();
        let item = list.find((s) => s.id === notificationItemId);
        if (item === undefined) {
            throw 'Notification not found';
        }
        return item;
    }

    loadRequestSummaryListFromStorage(): RequestRoot[] {
        let summaryList: RequestRoot[];
        let item = localStorage.getItem(this.STORAGE_SUMMARY_KEY);
        if (item) {
            let json = JSON.parse(item);
            summaryList = RequestRoot.readObjects(
                json.data,
                this.featuresF,
                this.featureTreeReaderF
            );
        } else {
            summaryList = [];
            this.storeRequestSummaryListToStorage(summaryList);
        }
        return summaryList;
    }

    loadRequestDataFromStorage(requestId: string): RequestRoot {
        let key = this.STORAGE_REQUEST_KEY_PREFIX + requestId;
        let item = localStorage.getItem(key);

        if (item) {
            let json = JSON.parse(item);
            json = this.upgradeRequestDataAndSave(json);
            let x = RequestRoot.readObject(
                json.data,
                this.featuresF,
                this.featureTreeReaderF
            );
            return x
        } else {
            throw Error('Request not found');
        }
    }

    storeNotificationListToStorage(
        notifications: ApplicationNotification[]
    ): void {
        let serializedNotifications: any[] = [];
        notifications.forEach((n) => {
            let type = this.notificationTypes.get(n.constructor.name);
            serializedNotifications.push({
                key: n.constructor.name,
                value: this.converter.serialize(n, type),
            });
        });
        let stringlified = JSON.stringify({
            data: serializedNotifications,
            version: this.CURRENT_NOTIFICATION_VERSION,
        });
        localStorage.setItem(this.STORAGE_APP_NOTIFICATIONS, stringlified);
    }

    storeNotificationItemToStorage(
        notificationItem: ApplicationNotification
    ): void {
        let list = this.loadNotificationListFromStorage();
        let index = list.findIndex((s) => s.id === notificationItem.id);
        if (index > -1) {
            list[index] = notificationItem;
        } else {
            list.push(notificationItem);
        }
        this.storeNotificationListToStorage(list);
    }

    storeRequestSummaryListToStorage(summaryList: RequestRoot[]): void {
        let serializedSummary = this.converter.serializeArray(
            summaryList,
            RequestRoot
        );
        let item = JSON.stringify({
            data: serializedSummary,
            version: this.CURRENT_REQUEST_SUMMARY_VERSION,
        });
        localStorage.setItem(this.STORAGE_SUMMARY_KEY, item);
    }

    storeSummaryItemToStorage(summaryItem: RequestRoot): void {
        let list = this.loadRequestSummaryListFromStorage();
        let index = list.findIndex((s) => s.id === summaryItem.id);
        if (index > -1) {
            list[index] = summaryItem;
        } else {
            list.push(summaryItem);
        }
        this.storeRequestSummaryListToStorage(list);
    }

    storeRequestDataToStorage(requestData: RequestRoot): void {
        let key = this.STORAGE_REQUEST_KEY_PREFIX + requestData.id;
        let serialized = this.converter.serialize(requestData, RequestRoot);

        localStorage.setItem(
            key,
            JSON.stringify({
                data: serialized,
                version: this.CURRENT_REQUEST_DATA_VERSION,
            })
        );
    }

    removeRequestSummaryItemFromStorage(requestId?: string): void {
        let list = this.loadRequestSummaryListFromStorage();
        list.splice(
            list.findIndex((s) => requestId == s.id),
            1
        );
        this.storeRequestSummaryListToStorage(list);
    }

    removeRequestDataFromStorage(requestId?: string): void {
        let key = this.STORAGE_REQUEST_KEY_PREFIX + requestId;
        localStorage.removeItem(key);
    }

    removeNotificationFromStorage(notificationId?: string): void {
        let list = this.loadNotificationListFromStorage();
        list.splice(
            list.findIndex((n) => n.id === notificationId),
            1
        );
        this.storeNotificationListToStorage(list);
    }

    private upgradeRequestDataAndSave(legacy: any): any {
        let upgraded = { version: legacy.version, data: legacy.data };
        if (upgraded.version === this.CURRENT_REQUEST_DATA_VERSION) {
            return upgraded;
        }

        let request = RequestRoot.readObject(
            upgraded.data,
            this.featuresF,
            this.featureTreeReaderF
        );

        if (upgraded.version === '1') {
            request.writeBlob('applicant.printPhoto');
            upgraded.version = '2';
        }

        this.storeRequestDataToStorage(request);
        upgraded.version = this.CURRENT_REQUEST_DATA_VERSION;
        upgraded.data = this.converter.serialize(request, RequestRoot);
        return upgraded;
    }
}
